import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, BadgeCheck, Eye, Sparkles, UserPlus } from "lucide-react";
import { API_URL } from "../config";
import { SPECIALTIES, useSpecialty } from "../SpecialtyContext";

/**
 * Витрина именного отличия портала: за каждым разделом стоит конкретный
 * практикующий врач, а не анонимный алгоритм. Показывает назначенных
 * ответственных редакторов с реальной статистикой их публикаций и честно
 * перечисляет ещё не занятые разделы как приглашение.
 */
export default function EditorialBoard() {
  const [editors, setEditors] = useState(null);
  const { setSpecialty } = useSpecialty();

  useEffect(() => {
    fetch(`${API_URL}/specialty-editors`)
      .then(r => r.ok ? r.json() : [])
      .then(async list => {
        // Обогащаем карточки реальной статистикой из публичного профиля —
        // список редакторов недублирует то, что уже считает /doctors/{id}.
        const enriched = await Promise.all(list.map(async e => {
          const r = await fetch(`${API_URL}/doctors/${e.editor_id}`);
          const profile = r.ok ? await r.json() : null;
          return { ...e, stats: profile?.stats || null };
        }));
        setEditors(enriched);
      })
      .catch(() => setEditors([]));
  }, []);

  const goVacant = id => {
    const s = SPECIALTIES.find(x => x.id === id);
    if (s) setSpecialty(s);
  };

  if (editors === null) return <section><p style={{ color: "#888" }}>Загрузка…</p></section>;

  const assignedIds = new Set(editors.map(e => e.specialty));
  const vacant = SPECIALTIES.filter(s => !assignedIds.has(s.id));

  return (
    <section style={{ maxWidth: 880 }}>
      <h2 className="section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Award size={20} strokeWidth={2.2} color="#e5005b" /> Редакция портала
      </h2>
      <p style={{ color: "#4a4a68", marginTop: -8, marginBottom: 24, fontSize: "1.02rem", lineHeight: 1.6, maxWidth: 680 }}>
        За каждым разделом «Современного Врача» стоит конкретный практикующий врач, а не
        безликая лента. Ответственный редактор отвечает за раздел под своим именем — отбирает материалы,
        разбирает случаи, отвечает коллегам.
      </p>

      {editors.length === 0 ? (
        <p style={{ color: "#888", marginBottom: 26 }}>Пока ни один раздел не занят — станьте первым.</p>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16, marginBottom: 34 }}>
          {editors.map(e => (
            <Link key={`${e.specialty}_${e.editor_id}`} to={`/doctors/${e.editor_id}`} className="card"
              style={{ textDecoration: "none", color: "inherit", display: "block" }}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                {e.avatar_url ? (
                  <img src={e.avatar_url} alt="" style={{ width: 56, height: 56, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                ) : (
                  <div style={{
                    width: 56, height: 56, borderRadius: "50%", background: "#007c84", color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700,
                    fontSize: "1.3rem", flexShrink: 0,
                  }}>
                    {(e.display_name || "?")[0].toUpperCase()}
                  </div>
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: "0.76rem", color: "#e5005b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                    {SPECIALTIES.find(s => s.id === e.specialty)?.label || e.specialty}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, fontWeight: 700, color: "#1f2937", fontSize: "1rem", marginTop: 2 }}>
                    {e.display_name}
                    {e.verification_status === "verified" && <BadgeCheck size={15} strokeWidth={2.4} color="#15803d" />}
                  </div>
                  {e.bio && (
                    <div style={{ color: "#666", fontSize: "0.84rem", marginTop: 3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {e.bio}
                    </div>
                  )}
                </div>
              </div>
              {e.stats && (
                <div style={{ display: "flex", gap: 14, marginTop: 12, paddingTop: 10, borderTop: "1px solid #eef0f6" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.82rem", color: "#888" }}>
                    <Eye size={13} strokeWidth={2.2} /> {e.stats.total_views} просмотров
                  </span>
                  {e.stats.total_citations > 0 && (
                    <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.82rem", color: "#e5005b" }}>
                      <Sparkles size={13} strokeWidth={2.2} /> {e.stats.total_citations} цитирований ИИ
                    </span>
                  )}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}

      {vacant.length > 0 && (
        <div style={{ background: "#fff1f6", border: "1px solid #ffc2d9", borderRadius: 12, padding: "16px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, color: "#9c0040", marginBottom: 8 }}>
            <UserPlus size={16} strokeWidth={2.2} /> Ищем ответственных редакторов
          </div>
          <p style={{ color: "#9c0040", fontSize: "0.88rem", margin: "0 0 12px", lineHeight: 1.5 }}>
            Эти разделы пока не заняты. Откликнуться можно прямо на странице раздела.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {vacant.map(s => (
              <Link key={s.id} to="/" onClick={() => goVacant(s.id)}
                style={{
                  background: "#fff", border: "1px solid #ff8fb8", color: "#9c0040", borderRadius: 999,
                  padding: "5px 14px", fontSize: "0.85rem", fontWeight: 600, textDecoration: "none",
                }}>
                {s.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
